import { Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Phone, Mail, MapPin, Calendar, ArrowUpRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { siteConfig } from '@/lib/siteConfig';
import { SEOSchema } from '@/components/SEOSchema';
import { Helmet } from 'react-helmet-async';
import { useJsonLd } from '@/hooks/useJsonLd';

const CONTACT_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'AccountingService',
      '@id': 'https://kgob.com/contact#charlotte-hq',
      name: 'Kohari Gonzalez Oneyear & Brown PLLC — Charlotte HQ',
      url: 'https://kgob.com/contact#charlotte-hq',
      telephone: '+1-704-599-3355',
      priceRange: '$$$',
      hasMap: 'https://maps.app.goo.gl/ivNjbD5tGZ1J8YW4A',
      address: { '@type': 'PostalAddress', streetAddress: '2740 East WT Harris Blvd, Suite 200', addressLocality: 'Charlotte', addressRegion: 'NC', postalCode: '28213', addressCountry: 'US' },
      geo: { '@type': 'GeoCoordinates', latitude: 35.2775429, longitude: -80.7276954 },
    },
    {
      '@type': 'AccountingService',
      '@id': 'https://kgob.com/contact#winston-salem',
      name: 'Kohari Gonzalez Oneyear & Brown PLLC — Winston-Salem',
      url: 'https://kgob.com/contact#winston-salem',
      telephone: '+1-336-761-0366',
      priceRange: '$$$',
      hasMap: 'https://maps.app.goo.gl/Hk5YfVMiNR6sD4s5A',
      address: { '@type': 'PostalAddress', streetAddress: '326 S. Main Street', addressLocality: 'Winston-Salem', addressRegion: 'NC', postalCode: '27101', addressCountry: 'US' },
      geo: { '@type': 'GeoCoordinates', latitude: 36.0914455, longitude: -80.2430443 },
    },
    {
      '@type': 'AccountingService',
      '@id': 'https://kgob.com/contact#southpark',
      name: 'Kohari Gonzalez Oneyear & Brown PLLC — SouthPark',
      url: 'https://kgob.com/contact#southpark',
      hasMap: 'https://www.google.com/maps/place/Kohari+Gonzalez+Oneyear+%26+Brown+PLLC/@35.1502976,-81.9938999,9z/data=!4m10!1m2!2m1!1sKohari+Gonzalez+Oneyear+%26+Brown+PLLC!3m6!1s0x88569f06533c2d93:0xe872b0cf6adf964c!8m2!3d35.1502976!4d-80.8403354!15sCiRLb2hhcmkgR29uemFsZXogT25leWVhciAmIEJyb3duIFBMTEOSARtjZXJ0aWZpZWRfcHVibGljX2FjY291bnRhbnTgAQA!16s%2Fg%2F11z5pmkylr?entry=ttu',
      telephone: '+1-704-599-3355',
      priceRange: '$$$',
      address: { '@type': 'PostalAddress', streetAddress: '6000 Fairview Road, Suite 1200', addressLocality: 'Charlotte', addressRegion: 'NC', postalCode: '28210', addressCountry: 'US' },
    },
    {
      '@type': 'AccountingService',
      '@id': 'https://kgob.com/contact#mooresville',
      name: 'Kohari Gonzalez Oneyear & Brown PLLC — Mooresville',
      url: 'https://kgob.com/contact#mooresville',
      hasMap: 'https://www.google.com/maps/place/Kohari+Gonzalez+Oneyear+%26+Brown+PLLC/@35.5927167,-80.8649103,17z/data=!3m1!4b1!4m6!3m5!1s0x885154e6f8628865:0xee1852291a26c59d!8m2!3d35.5927124!4d-80.8623354!16s%2Fg%2F1tftrssr?entry=ttu',
      telephone: '+1-704-663-6213',
      priceRange: '$$$',
      address: { '@type': 'PostalAddress', streetAddress: '121 Rolling Hill Road', addressLocality: 'Mooresville', addressRegion: 'NC', postalCode: '28117', addressCountry: 'US' },
    },
    {
      '@type': 'AccountingService',
      '@id': 'https://kgob.com/contact#pittsburgh',
      name: 'Kohari Gonzalez Oneyear & Brown PLLC — Pittsburgh',
      url: 'https://kgob.com/contact#pittsburgh',
      telephone: '+1-412-254-6280',
      priceRange: '$$$',
      hasMap: 'https://www.google.com/maps/search/?api=1&query=651+Holiday+Drive+Plaza+5+Suite+400+Pittsburgh+PA+15220',
      address: { '@type': 'PostalAddress', streetAddress: '651 Holiday Drive, Plaza 5 Suite 400', addressLocality: 'Pittsburgh', addressRegion: 'PA', postalCode: '15220', addressCountry: 'US' },
    },
  ],
};
import LeadFormMultiStep from '@/components/forms/LeadFormMultiStep';

const Contact = () => {
  // Imperatively inject schema (Helmet v2 silently drops <script> content)
  useJsonLd('contact-offices', CONTACT_SCHEMA);

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>Contact Us | KGOB CPAs & Advisors</title>
        <meta name="description" content="Get in touch with KGOB CPAs & Advisors. Schedule a consultation or reach out to our team for expert financial advisory services." />
        <link rel="canonical" href="https://kgob.com/contact" />
        <meta property="og:title" content="Contact Us | KGOB CPAs & Advisors" />
        <meta property="og:description" content="Get in touch with KGOB CPAs & Advisors in Charlotte, NC. Schedule a consultation for tax, accounting, or IRS help." />
        <meta property="og:url" content="https://kgob.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://kgob.com/share/og/v1/kgob-share-default.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Us | KGOB CPAs &amp; Advisors" />
        <meta name="twitter:description" content="Get in touch with KGOB CPAs &amp; Advisors. Schedule a consultation by phone, or send a message." />
        <meta name="twitter:image" content="https://kgob.com/share/og/v1/kgob-twitter-card.png" />
      </Helmet>
      <SEOSchema />
      <Header />
      <main>
        {/* Hero */}
        <section className="bg-[linear-gradient(180deg,#0B2240_0%,#07182F_100%)] text-white py-16 lg:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <p className="uppercase tracking-[0.18em] text-xs text-[#E3C96E] mb-3">
                LET'S CONNECT
              </p>
              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight">
                Start Your Growth Journey
              </h1>
              <p className="mt-4 text-lg text-white/80 max-w-2xl mx-auto">
                Ready to elevate your financial strategy? Our team is here to help.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Content */}
        <section className="py-16 lg:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Contact Info */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="lg:col-span-2 space-y-8"
              >
                <Card className="rounded-2xl bg-white p-8 shadow-[0_8px_30px_rgba(11,34,64,0.12)]">
                  <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-6">
                    Get in Touch
                  </h2>
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                        <Phone className="h-5 w-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#0B2240] mb-1">Phone</h3>
                        <a data-testid="contact-a-9" 
                          href={`tel:${siteConfig.phone}`}
                          className="text-[#4C5A70] hover:text-[#D4AF37] transition-colors"
                        >
                          {siteConfig.phoneDisplay}
                        </a>
                        <p className="text-sm text-[#4C5A70]">{siteConfig.supportHours.weekday}</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                        <Mail className="h-5 w-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#0B2240] mb-1">Email</h3>
                        <a data-testid="contact-a-8" 
                          href={`mailto:${siteConfig.email}`}
                          className="text-[#4C5A70] hover:text-[#D4AF37] transition-colors"
                        >
                          {siteConfig.email}
                        </a>
                        <p className="text-sm text-[#4C5A70]">We respond within 1 business day</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                        <MapPin className="h-5 w-5 text-[#D4AF37]" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-[#0B2240] mb-3">Our Offices</h3>
                        
                        {/* Charlotte HQ */}
                        <div id="charlotte-hq" className="mb-4 pb-4 border-b border-gray-200 scroll-mt-20">
                          <p className="font-semibold text-[#0B2240] mb-1">Charlotte HQ</p>
                          <address className="not-italic text-[#4C5A70] text-sm">
                            <div>2740 East WT Harris Blvd, Suite 200</div>
                            <div>Charlotte, NC 28213</div>
                            <a data-testid="contact-a-7" href="tel:7045993355" className="text-[#D4AF37] hover:underline">
                              (704) 599-3355
                            </a>
                          </address>
                          <a
                            href="https://maps.app.goo.gl/ivNjbD5tGZ1J8YW4A"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-2 text-xs text-[#0B2240] font-semibold hover:text-[#D4AF37] transition-colors"
                            data-testid="contact-directions-charlotte-hq"
                          >
                            Get Directions <ArrowUpRight size={12} strokeWidth={2.5} />
                          </a>
                        </div>
                        
                        {/* Winston-Salem */}
                        <div id="winston-salem" className="mb-4 pb-4 border-b border-gray-200 scroll-mt-20">
                          <p className="font-semibold text-[#0B2240] mb-1">Winston-Salem</p>
                          <address className="not-italic text-[#4C5A70] text-sm">
                            <div>326 S. Main Street</div>
                            <div>Winston-Salem, NC 27101</div>
                            <a data-testid="contact-a-6" href="tel:3367610366" className="text-[#D4AF37] hover:underline">
                              (336) 761-0366
                            </a>
                          </address>
                          <a
                            href="https://maps.app.goo.gl/Hk5YfVMiNR6sD4s5A"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-2 text-xs text-[#0B2240] font-semibold hover:text-[#D4AF37] transition-colors"
                            data-testid="contact-directions-winston-salem"
                          >
                            Get Directions <ArrowUpRight size={12} strokeWidth={2.5} />
                          </a>
                        </div>
                        
                        {/* SouthPark */}
                        <div id="southpark" className="mb-4 pb-4 border-b border-gray-200 scroll-mt-20">
                          <p className="font-semibold text-[#0B2240] mb-1">SouthPark <span className="text-xs text-[#4C5A70]">(By Appointment)</span></p>
                          <address className="not-italic text-[#4C5A70] text-sm">
                            <div>6000 Fairview Road, Suite 1200</div>
                            <div>Charlotte, NC 28210</div>
                            <a data-testid="contact-a-5" href="tel:7045993355" className="text-[#D4AF37] hover:underline">
                              (704) 599-3355
                            </a>
                          </address>
                          <a
                            href="https://www.google.com/maps/place/Kohari+Gonzalez+Oneyear+%26+Brown+PLLC/@35.1502976,-81.9938999,9z/data=!4m10!1m2!2m1!1sKohari+Gonzalez+Oneyear+%26+Brown+PLLC!3m6!1s0x88569f06533c2d93:0xe872b0cf6adf964c!8m2!3d35.1502976!4d-80.8403354!15sCiRLb2hhcmkgR29uemFsZXogT25leWVhciAmIEJyb3duIFBMTEOSARtjZXJ0aWZpZWRfcHVibGljX2FjY291bnRhbnTgAQA!16s%2Fg%2F11z5pmkylr?entry=ttu&g_ep=EgoyMDI2MDcyNy4wIKXMDSoASAFQAw%3D%3D"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-2 text-xs text-[#0B2240] font-semibold hover:text-[#D4AF37] transition-colors"
                            data-testid="contact-directions-southpark"
                          >
                            Get Directions <ArrowUpRight size={12} strokeWidth={2.5} />
                          </a>
                        </div>
                        
                        {/* Mooresville */}
                        <div id="mooresville" className="mb-4 pb-4 border-b border-gray-200 scroll-mt-20">
                          <p className="font-semibold text-[#0B2240] mb-1">Mooresville <span className="text-xs text-[#4C5A70]">(By Appointment)</span></p>
                          <address className="not-italic text-[#4C5A70] text-sm">
                            <div>121 Rolling Hill Road</div>
                            <div>Mooresville, NC 28117</div>
                            <a data-testid="contact-a-4" href="tel:7046636213" className="text-[#D4AF37] hover:underline">
                              (704) 663-6213
                            </a>
                          </address>
                          <a
                            href="https://www.google.com/maps/place/Kohari+Gonzalez+Oneyear+%26+Brown+PLLC/@35.5927167,-80.8649103,17z/data=!3m1!4b1!4m6!3m5!1s0x885154e6f8628865:0xee1852291a26c59d!8m2!3d35.5927124!4d-80.8623354!16s%2Fg%2F1tftrssr?entry=ttu&g_ep=EgoyMDI2MDcyNy4wIKXMDSoASAFQAw%3D%3D"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-2 text-xs text-[#0B2240] font-semibold hover:text-[#D4AF37] transition-colors"
                            data-testid="contact-directions-mooresville"
                          >
                            Get Directions <ArrowUpRight size={12} strokeWidth={2.5} />
                          </a>
                        </div>

                        {/* Pittsburgh */}
                        <div id="pittsburgh" className="scroll-mt-20">
                          <p className="font-semibold text-[#0B2240] mb-1">Pittsburgh <span className="text-xs text-[#4C5A70]">(By Appointment)</span></p>
                          <address className="not-italic text-[#4C5A70] text-sm">
                            <div>651 Holiday Drive, Plaza 5 Suite 400</div>
                            <div>Pittsburgh, PA 15220</div>
                            <a data-testid="contact-a-3" href="tel:4122546280" className="text-[#D4AF37] hover:underline">
                              (412) 254-6280
                            </a>
                          </address>
                          <a
                            href="https://www.google.com/maps/search/?api=1&query=651+Holiday+Drive+Plaza+5+Suite+400+Pittsburgh+PA+15220"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-2 text-xs text-[#0B2240] font-semibold hover:text-[#D4AF37] transition-colors"
                            data-testid="contact-directions-pittsburgh"
                          >
                            Get Directions <ArrowUpRight size={12} strokeWidth={2.5} />
                          </a>
                        </div>

                        {/* Link to dedicated /locations page for full office detail + reviews */}
                        <div className="mt-4 pt-3 border-t border-gray-200">
                          <Link
                            to="/locations"
                            className="text-xs text-[#0B2240] font-semibold hover:text-[#D4AF37] inline-flex items-center gap-1 transition-colors"
                            data-testid="contact-view-all-locations"
                          >
                            View all locations &amp; reviews <ArrowUpRight size={12} strokeWidth={2.5} />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Multi-Step Lead Form */}
                <LeadFormMultiStep />
              </motion.div>

              {/* Sidebar */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                {/* Schedule Card */}
                <Card className="rounded-2xl glass-card p-6">
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#0B2240] mb-4">
                    <Calendar className="h-6 w-6 text-[#D4AF37]" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0B2240] mb-3">
                    Schedule a Consultation
                  </h3>
                  <p className="text-sm text-[#4C5A70] mb-4">
                    Book a complimentary 30-minute consultation with one of our advisors.
                  </p>
                  <Button
                    asChild
                    className="w-full h-12 rounded-xl bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] hover:brightness-95 font-medium"
                    data-testid="contact-schedule-button"
                  >
                    <a data-testid="contact-a-2" href="https://app.kgob.com/book-consultation?source=contact-page">Schedule Now</a>
                  </Button>
                  <p className="text-xs text-[#4C5A70] mt-3 text-center">
                    Direct booking now available
                  </p>
                </Card>

                {siteConfig.portals.client && (
                  <Card className="rounded-2xl bg-[#0B2240] text-white p-6">
                    <h3 className="font-['Playfair_Display'] text-xl font-semibold mb-3">
                      Current Clients
                    </h3>
                    <p className="text-sm text-white/80 mb-4">
                      Access your secure portal for documents, reports, and messages.
                    </p>
                    <Button data-testid="contact-button-1"
                      asChild
                      variant="ghost"
                      className="w-full h-10 border border-white/20 text-white hover:bg-white/10"
                    >
                      <a data-testid="contact-a-1" href={siteConfig.portals.client} target="_blank" rel="noopener noreferrer">
                        Client Portal Login
                      </a>
                    </Button>
                  </Card>
                )}

                {/* Support Hours */}
                <Card className="rounded-2xl bg-white p-6 border">
                  <h3 className="font-semibold text-[#0B2240] mb-3">Support Hours</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#4C5A70]">Monday - Friday</span>
                      <span className="text-[#0B2240] font-medium">9am - 6pm</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#4C5A70]">Saturday</span>
                      <span className="text-[#0B2240] font-medium">10am - 2pm</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#4C5A70]">Sunday</span>
                      <span className="text-[#0B2240] font-medium">Closed</span>
                    </div>
                  </div>
                  <p className="text-xs text-[#4C5A70] mt-3">
                    All times Eastern (EST)
                  </p>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;